import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { claimReward, getLeaderboard, registerPlayer, submitScore } from './api';
import { createEngine, giftDataUrl, owlDataUrl, W } from './engine';
import { sound } from './sound';
import { t } from '../../i18n/i18n';
import './CandleClash.scss';

const owlSrc = owlDataUrl();
const giftSrc = giftDataUrl();
const SKELETON = Array.from({ length: 8 }, () => ({}));

const STORAGE_KEY = 'candleClashPlayer';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NICKNAME_RE = /^[\p{L}\p{N} _-]{2,16}$/u;

const money = (v) => `$${Math.round(v).toLocaleString('en-US')}`;
const shortMoney = (v) => (v >= 1000 ? `$${v / 1000}K` : `$${v}`);
const maskEmail = (email = '') => email.replace(/^(.).*(@.*)$/, '$1•••$2');

function Ladder({ tiers, score }) {
  return (
    <ol className="candle-clash__ladder" aria-label={t('Reward tiers')}>
      {tiers.map((t) => (
        <li key={t.min} className={score >= t.min ? 'is-reached' : ''}>
          <strong>{t.percent}</strong>
          <small>{shortMoney(t.min)}</small>
        </li>
      ))}
    </ol>
  );
}

function savedPlayer() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

export default function CandleClash() {
  const shellRef = useRef(null);
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const playerRef = useRef(null);
  const othersRef = useRef([]);
  const bestRef = useRef(0);
  const lastRunRef = useRef(null);

  const [phase, setPhase] = useState('gate');
  const [form, setForm] = useState(() => ({ nickname: '', email: '', ...savedPlayer() }));
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState([]);
  const [error, setError] = useState('');
  const [rewardState, setRewardState] = useState('idle');
  const [height, setHeight] = useState(0);
  const [muted, setMuted] = useState(sound.isMuted);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const refreshBoard = useCallback(async () => {
    const { entries } = await getLeaderboard({ playerId: playerRef.current.playerId });
    othersRef.current = entries.filter((e) => !e.isYou).map((e) => e.score);
    setBoard(entries);
  }, []);

  const saveRun = useCallback(async () => {
    setError('');
    setResult(null);
    try {
      const outcome = await submitScore({ playerId: playerRef.current.playerId, ...lastRunRef.current });
      bestRef.current = outcome.best;
      await refreshBoard();
      setResult(outcome);
    } catch (err) {
      setError(t(err.message));
    }
  }, [refreshBoard]);

  useLayoutEffect(() => {
    const measure = () => {
      if (phaseRef.current === 'playing') return;
      const rect = shellRef.current.getBoundingClientRect();
      setHeight(Math.min(1200, Math.max(700, Math.round((W * rect.height) / rect.width))));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (!height) return undefined;
    document.fonts?.load('12px "Press Start 2P"');
    const engine = createEngine(canvasRef.current, {
      height,
      getRank: (score) => playerRef.current && 1 + othersRef.current.filter((s) => s > score).length,
      onGameOver: (run) => {
        lastRunRef.current = run;
        setRewardState('idle');
        setPhase('over');
        saveRun();
      },
    });
    engineRef.current = engine;
    return engine.destroy;
  }, [saveRun, height]);

  useEffect(() => {
    if (phase !== 'over' || !result) return;
    document.querySelector('.candle-clash__row.is-you')?.scrollIntoView({ block: 'center' });
    if (result.reward) sound.reward();
  }, [phase, result]);

  const play = () => {
    setPhase('playing');
    engineRef.current.start(bestRef.current);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    sound.unlock();
    const nickname = form.nickname.trim().replace(/\s+/g, ' ');
    const email = form.email.trim().toLowerCase();
    if (!NICKNAME_RE.test(nickname)) return setFormError(t('Enter a nickname (2–16 letters or numbers).'));
    if (!EMAIL_RE.test(email)) return setFormError(t('Enter a valid email first.'));
    setFormError('');
    setBusy(true);
    try {
      const { playerId, best } = await registerPlayer({ nickname, email });
      playerRef.current = { playerId, nickname, email };
      bestRef.current = best;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ nickname, email }));
      await refreshBoard();
      play();
    } catch (err) {
      setFormError(t(err.message));
    } finally {
      setBusy(false);
    }
  };

  const sendReward = async () => {
    sound.ui();
    setRewardState('sending');
    try {
      await claimReward({ playerId: playerRef.current.playerId, rewardId: result.reward.id });
      setRewardState('sent');
      sound.success();
    } catch (err) {
      setError(t(err.message));
      setRewardState('idle');
    }
  };

  const youIndex = board.findIndex((e) => e.isYou);
  const ahead = result && youIndex > 0 ? board[youIndex - 1] : null;
  let chase = '';
  if (ahead) {
    chase = t('{{amount}} to pass {{name}}', {
      amount: money(ahead.score - board[youIndex].score + 1),
      name: ahead.nickname,
    });
  } else if (result && youIndex === 0) chase = t("You're #1");

  const tiers = result?.tiers ?? [];
  const nextTier = tiers.find((t) => t.min > lastRunRef.current.score);

  const field = (name) => ({
    value: form[name],
    onChange: (e) => setForm((f) => ({ ...f, [name]: e.target.value })),
  });

  return (
    <main className="candle-clash">
      <div className="candle-clash__shell" ref={shellRef}>
        <canvas ref={canvasRef} className="candle-clash__canvas" aria-label={t('Candle Clash game')} />

        {phase === 'gate' && (
          <section className="candle-clash__overlay candle-clash__gate" aria-label={t('Start Candle Clash')}>
            <span className="candle-clash__logo" style={{ '--icon': 'url(/icons/brand-mark.svg)' }} aria-hidden="true" />
            <img className="candle-clash__owl" src={owlSrc} alt="" />
            <h1 className="candle-clash__title">
              <span>CANDLE</span>
              <span>CLASH</span>
            </h1>
            <p className="candle-clash__subtitle">
              {t('Fly further. Climb the leaderboard.')}
              <strong>{t('Unlock rewards.')}</strong>
            </p>
            <form className="candle-clash__form" onSubmit={onSubmit} noValidate>
              <input {...field('nickname')} name="nickname" maxLength={16} placeholder={t('Nickname')} autoComplete="nickname" aria-label={t('Nickname')} />
              <input {...field('email')} name="email" type="email" placeholder={t('Enter your email')} autoComplete="email" aria-label={t('Email')} />
              <button type="submit" disabled={busy} aria-label={t('Start game')}>
                {busy ? '…' : <svg viewBox="0 0 12 10" width="1.6em" height="1.3em" shapeRendering="crispEdges" aria-hidden="true"><path fill="currentColor" d="M0 4h8V2h2v2h2v2h-2v2H8V6H0zM6 0h2v2H6zM6 8h2v2H6z" /></svg>}
              </button>
              <p className="candle-clash__error" aria-live="polite">{formError}</p>
            </form>
          </section>
        )}

        {phase === 'over' && (
          <section className="candle-clash__overlay candle-clash__over" aria-label={t('Game over')}>
            <p className="candle-clash__label">{t('LEADERBOARD')}</p>
            {result && (
              <dl className="candle-clash__summary">
                <div>
                  <dt>{t('SCORE')}</dt>
                  <dd>{money(lastRunRef.current.score)}</dd>
                </div>
                <div>
                  <dt>{t('RANK')}</dt>
                  <dd>#{result.rank}</dd>
                </div>
                <div className={result.isNewBest ? 'is-new' : ''}>
                  <dt>{result.isNewBest ? t('NEW BEST') : t('BEST')}</dt>
                  <dd>{money(result.best)}</dd>
                </div>
              </dl>
            )}
            {chase && <p className="candle-clash__chase">{chase}</p>}
            {error && !result ? (
              <div className="candle-clash__retry">
                <p>{error}</p>
                <button type="button" onClick={saveRun}>{t('Retry')}</button>
              </div>
            ) : (
              <ol className="candle-clash__board" aria-busy={!result}>
                {(result ? board : SKELETON).map((e, i) => (
                  <li
                    key={result ? `${e.rank}-${e.nickname}` : i}
                    style={{ '--i': Math.min(i, 12) }}
                    className={`candle-clash__row${result ? '' : ' is-loading'}${e.isYou ? ' is-you' : ''}${e.rank <= 3 ? ` is-top-${e.rank}` : ''}`}
                  >
                    <span className="candle-clash__rank">{e.rank}</span>
                    <span className="candle-clash__name">{e.nickname}</span>
                    {e.isYou && <span className="candle-clash__new">{t('YOU')}</span>}
                    <span className="candle-clash__score">{e.score != null && money(e.score)}</span>
                  </li>
                ))}
              </ol>
            )}

            {result?.reward ? (
              <div className="candle-clash__reward">
                <img className="candle-clash__gift" src={giftSrc} alt="" />
                <p className="candle-clash__unlocked">{t('REWARD UNLOCKED')}</p>
                <h2>
                  <strong>{result.reward.percent}</strong>
                  {t('off all evaluations')}
                </h2>
                <Ladder tiers={tiers} score={lastRunRef.current.score} />
                <p className="candle-clash__mailto">
                  {rewardState === 'sent'
                    ? t('Sent to {{email}}. Check your inbox.', { email: maskEmail(playerRef.current.email) })
                    : t('Your code will be emailed to {{email}}', { email: maskEmail(playerRef.current.email) })}
                </p>
                {error && <p className="candle-clash__error">{error}</p>}
                <button type="button" onClick={sendReward} disabled={rewardState !== 'idle'} className={rewardState === 'sent' ? 'is-sent' : ''}>
                  {{ idle: error ? t('Try again') : t('Email my reward'), sending: t('Sending…'), sent: t('Sent!') }[rewardState]}
                </button>
                <button type="button" className="candle-clash__link" onClick={play}>{t('Play Again')}</button>
              </div>
            ) : (
              <div className="candle-clash__next">
                {tiers.length > 0 && <Ladder tiers={tiers} score={lastRunRef.current.score} />}
                {nextTier && (
                  <p>
                    {t('Next reward: {{percent}} off at {{amount}}', {
                      percent: nextTier.percent,
                      amount: money(nextTier.min),
                    })}
                  </p>
                )}
                <button type="button" className="candle-clash__play" onClick={play} disabled={!result && !error}>
                  {t('Play Again')}
                </button>
              </div>
            )}
          </section>
        )}

        <button
          type="button"
          className="candle-clash__mute"
          aria-pressed={muted}
          aria-label={muted ? t('Unmute sound') : t('Mute sound')}
          onClick={() => {
            sound.setMuted(!muted);
            setMuted(!muted);
            if (muted) sound.ui();
          }}
        >
          <svg viewBox="0 0 12 10" width="1.5em" height="1.25em" shapeRendering="crispEdges" aria-hidden="true">
            <path fill="currentColor" d="M0 3h2l3-3h1v10H5L2 7H0z" />
            {muted ? <path fill="currentColor" d="M8 3h1v1h1V3h1v1h-1v1h1v1h-1V5h-1v1H8V5h1V4H8z" /> : <path fill="currentColor" d="M8 3h1v4H8zM10 1h1v8h-1z" />}
          </svg>
        </button>

        <footer className="candle-clash__ticker">
          <span>Alpha Capital</span>
          <span>Traders Build More</span>
        </footer>
      </div>
    </main>
  );
}
